import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useAuth, displayUser, isRestricted, isTeenAccount } from '../context/AuthContext'
import { useUnreadMessages } from '../context/UnreadMessagesContext'
import useNotifications from '../hooks/useNotifications'
import useSystemMessages from '../hooks/useSystemMessages'
import { useCoinBalance } from '../hooks/useCoinBalance'
import BugReportLink from './BugReportLink'
import ThemeToggle from './ThemeToggle'
import UserSearchDropdown from './UserSearchDropdown'
import UserSearchSheet from './UserSearchSheet'
import { decodeEntities } from '../lib/decodeEntities'

const NAV_LINKS = [
  { to: '/browse',    label: 'Browse',    auth: false },
  { to: '/feed',      label: 'Feed',      auth: true  },
  { to: '/activity',  label: 'Activity',  auth: true  },
  { to: '/requests',  label: 'Requests',  auth: true, requiresPrivacyMode: true },
  { to: '/quizzes',   label: 'Quizzes',   auth: false },
  { to: '/community', label: 'Community', auth: false },
]

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Navbar() {
  const location  = useLocation()
  const { user, profile, signOut } = useAuth()
  const isAdmin = profile?.isAdmin === true || profile?.isModerator === true
  const restricted = isRestricted(profile)
  const isTeen = isTeenAccount(profile)
  const currentUser = displayUser(user, profile)
  const unreadMessages = useUnreadMessages()

  const [mobileOpen, setMobileOpen]       = useState(false)
  const [navDropOpen, setNavDropOpen]     = useState(false)
  const [userMenuOpen, setUserMenuOpen]   = useState(false)
  const [notifOpen, setNotifOpen]         = useState(false)
  const [inboxOpen, setInboxOpen]         = useState(false)
  const userMenuRef    = useRef(null)
  const notifRef       = useRef(null)
  const inboxRef       = useRef(null)
  const navDropRef     = useRef(null)
  const notifPanelRef  = useRef(null)
  const inboxPanelRef  = useRef(null)
  const { notifications, unread, markAllRead, markRead, clearAll } = useNotifications(user)
  const { messages: sysMessages, unread: sysUnread, markRead: sysMarkRead, markAllRead: sysMarkAllRead } = useSystemMessages()
  const { balance: coinBalance, loading: coinLoading } = useCoinBalance()

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); setNavDropOpen(false) }, [location.pathname])

  // Close user dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
      const insideNotif =
        (notifRef.current && notifRef.current.contains(e.target)) ||
        (notifPanelRef.current && notifPanelRef.current.contains(e.target))
      if (!insideNotif) setNotifOpen(false)

      const insideInbox =
        (inboxRef.current && inboxRef.current.contains(e.target)) ||
        (inboxPanelRef.current && inboxPanelRef.current.contains(e.target))
      if (!insideInbox) setInboxOpen(false)
      if (navDropRef.current && !navDropRef.current.contains(e.target)) {
        setNavDropOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const linkClass = ({ isActive }) =>
    `navbar__link${isActive ? ' navbar__link--active' : ''}`

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <nav className="navbar">
        <div className="navbar__inner">

          {/* Logo */}
          <Link to="/" className="navbar__logo">
            <img src="/verse-icon.png" alt="Verse" className="navbar__logo-img" />
            <span className="gradient-text">Verse</span>
          </Link>

          {/* Mobile nav dropdown */}
          <div className="navbar__nav-drop" ref={navDropRef}>
            <button
              className="navbar__nav-drop-btn"
              onClick={() => setNavDropOpen(v => !v)}
              aria-label="Navigation"
            >
              <span /><span /><span />
            </button>
            {navDropOpen && (
              <div className="navbar__nav-drop-menu">
                {NAV_LINKS.filter(({ auth, requiresPrivacyMode }) => (!auth || currentUser) && (!requiresPrivacyMode || profile?.privacyMode === true)).map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `navbar__nav-drop-item${isActive ? ' navbar__nav-drop-item--active' : ''}`
                    }
                    onClick={() => setNavDropOpen(false)}
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Desktop nav links */}
          <ul className="navbar__links">
            {NAV_LINKS.filter(({ auth, requiresPrivacyMode }) => (!auth || currentUser) && (!requiresPrivacyMode || profile?.privacyMode === true)).map(({ to, label }) => (
              <li key={to}>
                <NavLink to={to} className={linkClass}>{label}</NavLink>
              </li>
            ))}
          </ul>

          <div className="navbar__spacer" />

          {/* Desktop right side */}
          <div className="navbar__actions">
            {currentUser ? (
              <>
                {/* Write button + separator */}
                {!restricted && (
                  <>
                    <Link to="/write" className="navbar__write-btn">
                      <WriteIcon /> Write
                    </Link>
                    <div className="navbar__separator" />
                  </>
                )}

                {/* Coin balance */}
                <Link to="/settings?tab=Payments" className="navbar__coins" title="Buy coins" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <span className="navbar__coins-icon">🪙</span>
                  <span className="navbar__coins-value">{coinLoading ? '…' : coinBalance.toLocaleString()}</span>
                </Link>

                {/* User search typeahead */}
                <UserSearchDropdown />
                <UserSearchSheet />

                {/* Notification bell */}
                <div className="navbar__notif-wrap" ref={notifRef}>
                  <button
                    className="navbar__icon-btn"
                    aria-label="Notifications"
                    onClick={() => { setNotifOpen(v => !v); setUserMenuOpen(false) }}
                  >
                    <BellIcon />
                  </button>
                  {unread > 0 && (
                    <span className="navbar__msg-badge">{unread > 99 ? '99+' : unread}</span>
                  )}
                </div>

                {/* System inbox */}
                <div className="navbar__notif-wrap" ref={inboxRef}>
                  <button
                    className="navbar__icon-btn"
                    aria-label="Inbox"
                    onClick={() => { setInboxOpen(v => !v); setNotifOpen(false); setUserMenuOpen(false) }}
                  >
                    <InboxIcon />
                  </button>
                  {sysUnread > 0 && (
                    <span className="navbar__msg-badge">{sysUnread > 99 ? '99+' : sysUnread}</span>
                  )}
                </div>

                {!isTeen && (
                  <div className="navbar__msg-wrap">
                    <Link to="/messages" className="navbar__icon-btn" aria-label="Messages">
                      <MessageIcon />
                    </Link>
                    {unreadMessages > 0 && (
                      <span className="navbar__msg-badge">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                  </div>
                )}

                {/* Avatar + dropdown */}
                <div className="navbar__user" ref={userMenuRef}>
                  <button
                    className={`navbar__avatar-btn${userMenuOpen ? ' navbar__avatar-btn--open' : ''}`}
                    style={{ background: currentUser.avatarUrl ? undefined : currentUser.grad }}
                    onClick={() => setUserMenuOpen((v) => !v)}
                    aria-label="User menu"
                    aria-expanded={userMenuOpen}
                  >
                    {currentUser.avatarUrl
                      ? <img src={currentUser.avatarUrl} alt={currentUser.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      : currentUser.initial}
                  </button>

                  {userMenuOpen && (
                    <div className="navbar__user-menu" role="menu">
                      <div className="navbar__user-menu__header">
                        <div className="navbar__user-menu__avatar" style={{ background: currentUser.avatarUrl ? undefined : currentUser.grad }}>
                          {currentUser.avatarUrl
                            ? <img src={currentUser.avatarUrl} alt={currentUser.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                            : currentUser.initial}
                        </div>
                        <div>
                          <div className="navbar__user-menu__name">{decodeEntities(currentUser.displayName)}</div>
                          <div className="navbar__user-menu__username">@{currentUser.username}</div>
                        </div>
                      </div>

                      <div className="navbar__user-menu__divider" />

                      <MenuItem to={`/user/${currentUser.username}`} icon="👤">My profile</MenuItem>
                      {!restricted && <MenuItem to="/write"                           icon="✍️">Write a story</MenuItem>}
                      <MenuItem to={`/user/${currentUser.username}?tab=stories`} icon="📚">My stories</MenuItem>
                      {!restricted && <MenuItem to="/drafts"                          icon="📝">My drafts</MenuItem>}
                      {!restricted && <MenuItem to="/write?import=true"               icon="↥">Import</MenuItem>}
                      {!restricted && <MenuItem to={`/user/${currentUser.username}?tab=announcements`} icon="📣">Announcements</MenuItem>}
                      <MenuItem to="/settings/profile"               icon="✏️">Edit profile</MenuItem>

                      <div className="navbar__user-menu__divider" />

                      {isAdmin && (
                        <MenuItem to="/admin" icon="🛡️">Admin</MenuItem>
                      )}
                      <MenuItem to="/settings" icon="⚙️">Settings</MenuItem>
                      <BugReportLink
                        className="navbar__menu-item"
                        label={<><span className="navbar__menu-item__icon">🐞</span>Report a bug</>}
                      />
                      <button
                        className="navbar__menu-item navbar__menu-item--danger"
                        role="menuitem"
                        onClick={() => { setUserMenuOpen(false); signOut() }}
                      >
                        <span className="navbar__menu-item__icon">🚪</span>
                        Log out
                      </button>
                    </div>
                  )}
                </div>

                <ThemeToggle style={{ marginLeft: 8 }} />
              </>
            ) : (
              <>
                <UserSearchDropdown />
                <UserSearchSheet />
                <Link to="/login"  className="navbar__link">Sign in</Link>
                <Link to="/signup" className="btn btn-primary" style={{ padding: '8px 18px', fontSize: '0.875rem' }}>
                  Start writing
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className={`navbar__hamburger${mobileOpen ? ' navbar__hamburger--open' : ''}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Backdrop */}
      {mobileOpen && (
        <div className="mobile-menu__backdrop" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="mobile-menu" role="dialog" aria-label="Navigation">
          {currentUser ? (
            <div className="mobile-menu__user">
              <div className="mobile-menu__avatar" style={{ background: currentUser.avatarUrl ? undefined : currentUser.grad }}>
                {currentUser.avatarUrl
                  ? <img src={currentUser.avatarUrl} alt={currentUser.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : currentUser.initial}
              </div>
              <div>
                <div className="mobile-menu__name">{currentUser.displayName}</div>
                <div className="mobile-menu__username">@{currentUser.username}</div>
              </div>
              <ThemeToggle style={{ marginLeft: 'auto' }} />
            </div>
          ) : (
            <div className="mobile-menu__user">
              <Link to="/login"  className="btn btn-primary" style={{ flex: 1 }} onClick={() => setMobileOpen(false)}>Sign in</Link>
              <Link to="/signup" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setMobileOpen(false)}>Sign up</Link>
            </div>
          )}

          <div className="mobile-menu__search">
            <UserSearchDropdown
              className="user-search--block"
              onNavigate={() => setMobileOpen(false)}
            />
          </div>

          <nav className="mobile-menu__nav">
            {NAV_LINKS.filter(({ auth, requiresPrivacyMode }) => (!auth || currentUser) && (!requiresPrivacyMode || profile?.privacyMode === true)).map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `mobile-menu__link${isActive ? ' mobile-menu__link--active' : ''}`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {currentUser && (
            <>
              <div className="mobile-menu__divider" />
              <div className="mobile-menu__link" style={{ cursor: 'default' }}>
                🪙 Coins: <strong>{coinLoading ? '…' : coinBalance.toLocaleString()}</strong>
              </div>
              {isAdmin && <Link to="/admin" className="mobile-menu__link">🛡️ Admin</Link>}
              {!restricted && (
                <>
                  <Link to="/write"    className="mobile-menu__link mobile-menu__link--write" onClick={() => setMobileOpen(false)}>✍️ Write a story</Link>
                  <Link to="/drafts" className="mobile-menu__link" onClick={() => setMobileOpen(false)}>📝 My drafts</Link>
                  <Link to="/write?import=true" className="mobile-menu__link" onClick={() => setMobileOpen(false)}>↥ Import</Link>
                </>
              )}
              <Link to={`/user/${currentUser.username}`} className="mobile-menu__link">👤 My profile</Link>
              <Link to="/feed"    className="mobile-menu__link">🏠 My feed</Link>
              <Link to={`/user/${currentUser.username}?tab=announcements`} className="mobile-menu__link">📣 Announcements</Link>
              <Link to="/settings/profile" className="mobile-menu__link">✏️ Edit profile</Link>
              <Link to="/settings" className="mobile-menu__link">⚙️ Settings</Link>
              <BugReportLink
                className="mobile-menu__link"
                style={{ width: '100%', textAlign: 'left', textDecoration: 'none' }}
              />
              <button
                className="mobile-menu__link mobile-menu__link--danger"
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => { setMobileOpen(false); signOut() }}
              >
                🚪 Log out
              </button>
            </>
          )}
        </div>
      )}

      {/* Notification & inbox panels — rendered at top level so they
          work from both the desktop bell and the mobile hamburger menu.
          The wrap divs hold refs the outside-click handler uses. */}
      {currentUser && notifOpen && (
        <div ref={notifPanelRef}>
          <NotificationPanel
            notifications={notifications}
            onMarkAllRead={markAllRead}
            onMarkRead={markRead}
            onClearAll={clearAll}
            onClose={() => setNotifOpen(false)}
            currentUser={currentUser}
          />
        </div>
      )}
      {currentUser && inboxOpen && (
        <div ref={inboxPanelRef}>
          <SystemInboxPanel
            messages={sysMessages}
            onMarkAllRead={sysMarkAllRead}
            onMarkRead={sysMarkRead}
            onClose={() => setInboxOpen(false)}
          />
        </div>
      )}

    </>
  )
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function MenuItem({ to, icon, badge, danger, children }) {
  return (
    <Link
      to={to}
      className={`navbar__menu-item${danger ? ' navbar__menu-item--danger' : ''}`}
      role="menuitem"
    >
      <span className="navbar__menu-item__icon">{icon}</span>
      {children}
      {badge != null && (
        <span className="msg-badge" style={{ marginLeft: 'auto' }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  )
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  )
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function notifMessage(n) {
  const actor = n.actor?.display_name || n.actor?.username || 'Someone'
  switch (n.type) {
    case 'comment_on_story':
      return <><strong>{actor}</strong> commented on your story <em>{n.meta?.story_title}</em></>
    case 'comment_on_quiz':
      return <><strong>{actor}</strong> commented on your quiz <em>{n.meta?.quiz_title}</em></>
    case 'reply_to_comment':
      return <><strong>{actor}</strong> replied to your comment</>
    case 'story_updated':
      return <><em>{n.meta?.story_title}</em> — Chapter {n.meta?.chapter_number}: {n.meta?.chapter_title}</>
    case 'group_post':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} posted in <em>{n.meta?.group_name}</em></>
      }
      return <><strong>{actor}</strong> posted in <em>{n.meta?.group_name}</em></>
    case 'group_post_reply':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} replied to your post in <em>{n.meta?.group_name}</em></>
      }
      return <><strong>{actor}</strong> replied to your post in <em>{n.meta?.group_name}</em></>
    case 'tip_received':
      return <><strong>{actor}</strong> tipped {n.meta?.amount} coins on <em>{n.meta?.story_title}</em></>
    case 'new_follower':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} followed you</>
      }
      return <><strong>{actor}</strong> followed you</>
    case 'follow_request':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} requested to follow you</>
      }
      return <><strong>{actor}</strong> requested to follow you</>
    case 'follow_request_accepted':
      return <><strong>{actor}</strong> accepted your follow request</>
    case 'like_story':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} liked your story <em>{n.meta?.story_title}</em></>
      }
      return <><strong>{actor}</strong> liked your story <em>{n.meta?.story_title}</em></>
    case 'like_quiz':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} liked your quiz <em>{n.meta?.quiz_title}</em></>
      }
      return <><strong>{actor}</strong> liked your quiz <em>{n.meta?.quiz_title}</em></>
    case 'like_comment':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} liked your comment</>
      }
      return <><strong>{actor}</strong> liked your comment</>
    case 'author_announcement':
      return <><strong>{actor}</strong> posted an announcement</>
    case 'wall_comment':
      return <><strong>{actor}</strong> commented on your wall post</>
    case 'wall_comment_reply':
      return <><strong>{actor}</strong> replied to your wall comment</>
    case 'like_wall_post':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} liked your wall post</>
      }
      return <><strong>{actor}</strong> liked your wall post</>
    case 'like_wall_comment':
      if (n._stackCount > 1) {
        return <><strong>{actor}</strong> and {n._stackCount - 1} {n._stackCount - 1 === 1 ? 'other' : 'others'} liked your wall comment</>
      }
      return <><strong>{actor}</strong> liked your wall comment</>
    case 'wall_mention':
      return <><strong>{actor}</strong> mentioned you in a wall post</>
    default:
      return 'New notification'
  }
}

function notifLink(n, currentUser) {
  if (n.type === 'author_announcement' && n.actor?.username) return `/user/${n.actor.username}?tab=announcements`
  if (n.type === 'new_follower' && currentUser?.username) return `/user/${currentUser.username}?tab=followers`
  if (n.type === 'follow_request') return '/requests'
  if (n.type === 'follow_request_accepted' && n.actor?.username) return `/user/${n.actor.username}`
  if ((n.type === 'group_post' || n.type === 'group_post_reply') && n.group_id) {
    const anchor = n.group_post_id ?? n.comment_id
    return `/community/${n.group_id}${anchor ? `#post-${anchor}` : ''}`
  }
  if (n.type === 'tip_received' && n.story_id) return `/story/${n.story_id}`
  if (n.type === 'like_story' && n.story_id) return `/story/${n.story_id}`
  if (n.type === 'like_quiz' && n.quiz_id) return `/quiz/${n.quiz_id}`
  if (n.type === 'like_comment' && n.comment_id) {
    if (n.story_id) return `/story/${n.story_id}#comment-${n.comment_id}`
    if (n.quiz_id)  return `/quiz/${n.quiz_id}#comment-${n.comment_id}`
  }
  if (n.type === 'comment_on_story' && n.story_id && n.comment_id) return `/story/${n.story_id}#comment-${n.comment_id}`
  if (n.type === 'reply_to_comment' && n.comment_id) {
    if (n.story_id) return `/story/${n.story_id}#comment-${n.comment_id}`
    if (n.quiz_id)  return `/quiz/${n.quiz_id}#comment-${n.comment_id}`
  }
  if (n.type === 'comment_on_quiz' && n.quiz_id && n.comment_id) return `/quiz/${n.quiz_id}#comment-${n.comment_id}`
  const wallTypes = new Set(['wall_comment', 'wall_comment_reply', 'like_wall_post', 'like_wall_comment', 'wall_mention'])
  if (wallTypes.has(n.type)) {
    let owner = n.meta?.wall_owner_username
    if (!owner) {
      console.warn('[notifLink] wall notification missing meta.wall_owner_username; falling back to actor', n.id, n.type)
      owner = n.actor?.username
    }
    if (owner) {
      const base = `/user/${owner}?tab=wall`
      if (n.wall_comment_id) return `${base}&comment=${n.wall_comment_id}`
      if (n.wall_post_id)    return `${base}&post=${n.wall_post_id}`
      return base
    }
  }
  if (n.story_id) return `/story/${n.story_id}`
  if (n.quiz_id)  return `/quiz/${n.quiz_id}`
  return '/'
}

function stackNotifications(notifications) {
  const result = []
  const stackMap = new Map() // key -> stacked notification

  for (const n of notifications) {
    let key = null
    if (n.type === 'group_post' && n.group_post_id) key = `group_post:${n.group_post_id}`
    else if (n.type === 'group_post_reply' && n.group_post_id) key = `group_post_reply:${n.group_post_id}`
    else if (n.type === 'new_follower') key = 'new_follower'
    else if (n.type === 'follow_request') key = 'follow_request:target'
    // follow_request_accepted intentionally NOT stackable — each acceptance
    // is individually meaningful and rare per pair.
    else if (n.type === 'like_story' && n.story_id) key = `like_story:${n.story_id}`
    else if (n.type === 'like_quiz' && n.quiz_id) key = `like_quiz:${n.quiz_id}`
    else if (n.type === 'like_comment' && n.comment_id) key = `like_comment:${n.comment_id}`
    else if (n.type === 'like_wall_post' && n.wall_post_id) key = `like_wall_post:${n.wall_post_id}`
    else if (n.type === 'like_wall_comment' && n.wall_comment_id) key = `like_wall_comment:${n.wall_comment_id}`

    if (key) {
      if (stackMap.has(key)) {
        const existing = stackMap.get(key)
        existing._stackCount += 1
        existing._stackedIds.push(n.id)
        if (!n.read) existing.read = false
      } else {
        const stacked = { ...n, _stackCount: 1, _stackedIds: [n.id], read: n.read }
        stackMap.set(key, stacked)
        result.push(stacked)
      }
    } else {
      result.push(n)
    }
  }
  return result
}

function ClearHistoryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      <line x1="10" y1="11" x2="10" y2="17"/>
      <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  )
}

function NotificationPanel({ notifications, onMarkAllRead, onMarkRead, onClearAll, onClose, currentUser }) {
  const navigate = useNavigate()
  const location = useLocation()
  const stackedNotifications = stackNotifications(notifications)
  const hasUnread = notifications.some(n => !n.read)
  const handleClear = async () => {
    await onClearAll()
    onClose()
  }

  function handleNotifClick(e, n) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
    e.preventDefault()
    if (!n.read) {
      if (n._stackedIds) n._stackedIds.forEach(id => onMarkRead(id))
      else onMarkRead(n.id)
    }
    onClose()
    const target = notifLink(n, currentUser)
    const currentFull = location.pathname + location.search + location.hash
    if (target !== currentFull) navigate(target)
    window.dispatchEvent(new CustomEvent('verse:notification-navigate', { detail: { target } }))
  }
  return (
    <div className="notif-panel">
      <div className="notif-panel__header">
        <span className="notif-panel__title">Notifications</span>
        <div className="notif-panel__header-actions">
          {hasUnread && (
            <button className="notif-panel__mark-all" onClick={onMarkAllRead}>
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button className="notif-panel__clear-btn" onClick={handleClear} title="Clear all notifications">
              <ClearHistoryIcon />
            </button>
          )}
        </div>
      </div>
      <div className="notif-panel__list">
        {notifications.length === 0 ? (
          <div className="notif-panel__empty">No notifications yet.</div>
        ) : (
          stackedNotifications.map(n => (
            <a
              key={n.id}
              href={notifLink(n, currentUser)}
              className={`notif-item${n.read ? '' : ' notif-item--unread'}`}
              onClick={e => handleNotifClick(e, n)}
            >
              {n.actor ? (
                <div className="notif-item__avatar" style={{ background: n.actor.avatar_url ? undefined : (n.actor.avatar_grad ?? 'linear-gradient(135deg,#7C3AED,#EC4899)') }}>
                  {n.actor.avatar_url
                    ? <img src={n.actor.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : (n.actor.display_name || n.actor.username || '?')[0].toUpperCase()}
                </div>
              ) : (
                <div className="notif-item__avatar notif-item__avatar--system">🔔</div>
              )}
              <div className="notif-item__body">
                <div className="notif-item__msg">{notifMessage(n)}</div>
                <div className="notif-item__time">{timeAgo(n.created_at)}</div>
              </div>
              {!n.read && <div className="notif-item__dot" />}
            </a>
          ))
        )}
      </div>
    </div>
  )
}

function MessageIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  )
}

function InboxIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  )
}

function SystemInboxPanel({ messages, onMarkAllRead, onMarkRead, onClose }) {
  const hasUnread = messages.some(m => !m.read)
  const [expanded, setExpanded] = useState(null)
  return (
    <div className="notif-panel inbox-panel">
      <div className="notif-panel__header">
        <span className="notif-panel__title">Inbox</span>
        {hasUnread && (
          <button className="notif-panel__mark-all" onClick={onMarkAllRead}>
            Mark all read
          </button>
        )}
      </div>
      <div className="notif-panel__list">
        {messages.length === 0 ? (
          <div className="notif-panel__empty">No messages from Verse.</div>
        ) : (
          messages.map(m => (
            <div
              key={m.id}
              className={`notif-item inbox-item${m.read ? '' : ' notif-item--unread'}`}
              onClick={() => {
                if (!m.read) onMarkRead(m.id)
                setExpanded(expanded === m.id ? null : m.id)
              }}
              style={{ cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="notif-item__avatar notif-item__avatar--system">📨</div>
                <div className="notif-item__body" style={{ flex: 1 }}>
                  <div className="notif-item__msg"><strong>{m.title}</strong></div>
                  <div className="notif-item__time">{timeAgo(m.createdAt)}</div>
                </div>
                {!m.read && <div className="notif-item__dot" />}
              </div>
              {expanded === m.id && (
                <div className="inbox-item__body">{m.body}</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function WriteIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )
}
